import { useState } from 'react';
import toast from 'react-hot-toast';
import { useAdminReferenceLists, useAddReferenceItem, useUpdateReferenceItem, useDeleteReferenceItem } from '../../../api/referenceLists';
import type { ReferenceItem } from '@spi/shared';
import { ConfirmDialog } from '../shared/ConfirmDialog';

const categories = [
  { id: 'crop', label: 'Crops' },
  { id: 'treatment', label: 'Treatments' },
  { id: 'zone', label: 'Zones' },
];

export function ReferenceListManager() {
  const { data: lists, isLoading } = useAdminReferenceLists();
  const addMutation = useAddReferenceItem();
  const updateMutation = useUpdateReferenceItem();
  const deleteMutation = useDeleteReferenceItem();

  const [activeCategory, setActiveCategory] = useState('crop');
  const [newValue, setNewValue] = useState('');
  const [newSortOrder, setNewSortOrder] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editSortOrder, setEditSortOrder] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; value: string } | null>(null);

  const items = lists?.[activeCategory] || [];

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    try {
      await addMutation.mutateAsync({ category: activeCategory, value: newValue.trim(), sortOrder: newSortOrder });
      toast.success('Item added');
      setNewValue('');
      setNewSortOrder(0);
    } catch {
      toast.error('Failed to add item');
    }
  };

  const startEdit = (item: ReferenceItem) => {
    setEditingId(item.id);
    setEditValue(item.value);
    setEditSortOrder(item.sortOrder);
  };

  const handleUpdate = async (id: number) => {
    try {
      await updateMutation.mutateAsync({ id, value: editValue, sortOrder: editSortOrder });
      toast.success('Updated');
      setEditingId(null);
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleToggleActive = async (item: ReferenceItem) => {
    try {
      await updateMutation.mutateAsync({ id: item.id, active: !item.active });
      toast.success(item.active ? 'Deactivated' : 'Activated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
    setDeleteTarget(null);
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" /></div>;
  }

  return (
    <div>
      {/* Category tabs */}
      <div className="flex gap-1 mb-6 bg-spi-seed rounded-xl p-1 max-w-sm">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeCategory === cat.id ? 'bg-white shadow text-spi-green-dark' : 'text-spi-slate hover:text-spi-charcoal'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Add form */}
      <div className="flex gap-2 mb-4">
        <input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={`New ${activeCategory}...`}
          className="input flex-1"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <input
          type="number"
          value={newSortOrder}
          onChange={(e) => setNewSortOrder(parseInt(e.target.value, 10) || 0)}
          placeholder="Sort"
          className="input w-20"
        />
        <button onClick={handleAdd} disabled={addMutation.isPending} className="btn btn-primary text-sm">
          Add
        </button>
      </div>

      {/* Items table */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-spi-seed">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-spi-charcoal">Value</th>
              <th className="text-left px-4 py-3 font-semibold text-spi-charcoal w-24">Sort Order</th>
              <th className="text-center px-4 py-3 font-semibold text-spi-charcoal w-20">Active</th>
              <th className="text-right px-4 py-3 font-semibold text-spi-charcoal w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className={`border-t border-spi-parchment ${!item.active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3">
                  {editingId === item.id ? (
                    <input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="input py-1 text-sm" />
                  ) : (
                    item.value
                  )}
                </td>
                <td className="px-4 py-3">
                  {editingId === item.id ? (
                    <input type="number" value={editSortOrder} onChange={(e) => setEditSortOrder(parseInt(e.target.value, 10) || 0)} className="input py-1 text-sm w-16" />
                  ) : (
                    item.sortOrder
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleToggleActive(item)}
                    className={`w-8 h-5 rounded-full transition-colors ${item.active ? 'bg-spi-green' : 'bg-gray-300'}`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${item.active ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  {editingId === item.id ? (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => handleUpdate(item.id)} className="text-spi-green text-xs font-semibold hover:underline">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-spi-slate text-xs hover:underline">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => startEdit(item)} className="text-spi-green text-xs font-semibold hover:underline">Edit</button>
                      <button onClick={() => setDeleteTarget({ id: item.id, value: item.value })} className="text-red-500 text-xs hover:underline">Delete</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-spi-slate">No items in this category</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Reference Item"
        message={`Are you sure you want to permanently delete "${deleteTarget?.value}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
